/** FHIR dateTime for a CareCommunication attachment, clock Europe/Copenhagen. */

const COPENHAGEN = 'Europe/Copenhagen';

/**
 * Clinio-Communicate contract for `payloads[].attachment.creation` (HTTP 400 otherwise).
 * FHIR R4 dateTime with zone: `Z` or `±hh:mm`. Max 64 chars.
 * Rejects date-only, a time without a zone, and `+0200`.
 */
export const ATTACHMENT_CREATION_MAX = 64;
export const ATTACHMENT_CREATION_DATETIME =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/;

/** The value when it matches the Communicate dateTime contract. Otherwise omitted. */
export function conformingAttachmentCreation(
  value: string | null | undefined,
): string | undefined {
  if (typeof value !== 'string' || value.length === 0 || value.length > ATTACHMENT_CREATION_MAX) {
    return undefined;
  }
  return ATTACHMENT_CREATION_DATETIME.test(value) ? value : undefined;
}

const CREATION_DATE = [
  0x2f, 0x43, 0x72, 0x65, 0x61, 0x74, 0x69, 0x6f, 0x6e, 0x44, 0x61, 0x74, 0x65,
] as const;

/** PDF date: D:YYYYMMDDHHmmSS, optional Z or ±HH'mm'. */
const PDF_DATE = /^D:(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(?:(Z)|([+-])(\d{2})'(\d{2})'?)?$/;

const MAX_STRING_BYTES = 128;

interface ZonedClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function zonedClock(instant: Date): ZonedClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: COPENHAGEN,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const num = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((part) => part.type === type)?.value;
    return value ? Number(value) : 0;
  };
  const hour = num('hour');
  return {
    year: num('year'),
    month: num('month'),
    day: num('day'),
    hour: hour === 24 ? 0 : hour,
    minute: num('minute'),
    second: num('second'),
  };
}

function offsetMinutes(instant: Date, clock: ZonedClock): number {
  const asUtc = Date.UTC(
    clock.year,
    clock.month - 1,
    clock.day,
    clock.hour,
    clock.minute,
    clock.second,
  );
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/**
 * `YYYY-MM-DDTHH:mm:ss±HH:mm` in Europe/Copenhagen (colon in the offset).
 * Example: `2024-09-12T16:00:00+02:00`. Never `Z`, never `+0200`, never no zone.
 */
export function formatCopenhagenFhirDateTime(instant: Date): string {
  const clock = zonedClock(instant);
  const offset = offsetMinutes(instant, clock);
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return (
    `${clock.year}-${pad(clock.month)}-${pad(clock.day)}` +
    `T${pad(clock.hour)}:${pad(clock.minute)}:${pad(clock.second)}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

function copenhagenWallToInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const firstOffset = offsetMinutes(new Date(utcGuess), zonedClock(new Date(utcGuess)));
  const first = new Date(utcGuess - firstOffset * 60_000);
  const secondOffset = offsetMinutes(first, zonedClock(first));
  return new Date(utcGuess - secondOffset * 60_000);
}

function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

function parsePdfDateToken(token: string): Date | null {
  const match = PDF_DATE.exec(
    token
      .replace(/^\uFEFF/, '')
      .replace(/\0/g, '')
      .trim(),
  );
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  if (!isRealCalendarDate(year, month, day)) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;

  const zulu = match[7];
  const sign = match[8];
  if (zulu === 'Z') {
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  }
  if (sign === '+' || sign === '-') {
    const offsetHour = Number(match[9]);
    const offsetMinute = Number(match[10]);
    if (offsetHour > 23 || offsetMinute > 59) return null;
    const total = (sign === '+' ? 1 : -1) * (offsetHour * 60 + offsetMinute);
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second) - total * 60_000);
  }
  return copenhagenWallToInstant(year, month, day, hour, minute, second);
}

function isWhitespace(byte: number | undefined): boolean {
  return (
    byte === 0x00 ||
    byte === 0x09 ||
    byte === 0x0a ||
    byte === 0x0c ||
    byte === 0x0d ||
    byte === 0x20
  );
}

function isDelimiter(byte: number | undefined): boolean {
  return (
    byte == null ||
    isWhitespace(byte) ||
    byte === 0x28 ||
    byte === 0x29 ||
    byte === 0x3c ||
    byte === 0x3e ||
    byte === 0x5b ||
    byte === 0x5d ||
    byte === 0x7b ||
    byte === 0x7d ||
    byte === 0x2f ||
    byte === 0x25
  );
}

function indexOfBytes(haystack: Uint8Array, needle: readonly number[], from: number): number {
  const last = haystack.length - needle.length;
  for (let i = from; i <= last; i += 1) {
    let matched = true;
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) {
        matched = false;
        break;
      }
    }
    if (matched) return i;
  }
  return -1;
}

function skipSpaceAndComments(bytes: Uint8Array, index: number): number {
  let i = index;
  while (i < bytes.length) {
    const byte = bytes[i];
    if (isWhitespace(byte)) {
      i += 1;
      continue;
    }
    if (byte === 0x25) {
      i += 1;
      while (i < bytes.length && bytes[i] !== 0x0a && bytes[i] !== 0x0d) i += 1;
      continue;
    }
    break;
  }
  return i;
}

function pdfBytesToText(raw: number[]): string | null {
  if (raw.length >= 2 && raw[0] === 0xfe && raw[1] === 0xff) {
    if ((raw.length - 2) % 2 !== 0) return null;
    let text = '';
    for (let i = 2; i < raw.length; i += 2) {
      text += String.fromCharCode(((raw[i] ?? 0) << 8) | (raw[i + 1] ?? 0));
    }
    return text;
  }
  return String.fromCharCode(...raw);
}

function readLiteral(bytes: Uint8Array, start: number): string | null {
  const raw: number[] = [];
  let depth = 1;
  let i = start + 1;
  while (i < bytes.length && raw.length <= MAX_STRING_BYTES) {
    const byte = bytes[i];
    if (byte == null) return null;
    if (byte === 0x5c) {
      const next = bytes[i + 1];
      if (next == null) return null;
      if (next === 0x6e) raw.push(0x0a);
      else if (next === 0x72) raw.push(0x0d);
      else if (next === 0x74) raw.push(0x09);
      else if (next === 0x62) raw.push(0x08);
      else if (next === 0x66) raw.push(0x0c);
      else if (next === 0x0a) {
        i += 2;
        continue;
      } else if (next === 0x0d) {
        i += bytes[i + 2] === 0x0a ? 3 : 2;
        continue;
      } else if (next >= 0x30 && next <= 0x37) {
        let octal = String.fromCharCode(next);
        let j = i + 2;
        for (let k = 0; k < 2 && j < bytes.length; k += 1, j += 1) {
          const digit = bytes[j];
          if (digit == null || digit < 0x30 || digit > 0x37) break;
          octal += String.fromCharCode(digit);
        }
        raw.push(Number.parseInt(octal, 8) & 0xff);
        i = j;
        continue;
      } else {
        raw.push(next);
      }
      i += 2;
      continue;
    }
    if (byte === 0x28) {
      depth += 1;
      raw.push(byte);
      i += 1;
      continue;
    }
    if (byte === 0x29) {
      depth -= 1;
      if (depth === 0) return pdfBytesToText(raw);
      raw.push(byte);
      i += 1;
      continue;
    }
    raw.push(byte);
    i += 1;
  }
  return null;
}

function hexValue(byte: number): number {
  if (byte >= 0x30 && byte <= 0x39) return byte - 0x30;
  if (byte >= 0x41 && byte <= 0x46) return byte - 0x41 + 10;
  if (byte >= 0x61 && byte <= 0x66) return byte - 0x61 + 10;
  return -1;
}

function readHex(bytes: Uint8Array, start: number): string | null {
  if (bytes[start + 1] === 0x3c) return null;
  const digits: number[] = [];
  let i = start + 1;
  while (i < bytes.length && digits.length <= MAX_STRING_BYTES * 2) {
    const byte = bytes[i];
    if (byte == null) return null;
    if (byte === 0x3e) {
      if (digits.length === 0) return null;
      if (digits.length % 2 === 1) digits.push(0x30);
      const raw: number[] = [];
      for (let d = 0; d < digits.length; d += 2) {
        const hi = hexValue(digits[d] ?? 0);
        const lo = hexValue(digits[d + 1] ?? 0);
        if (hi < 0 || lo < 0) return null;
        raw.push((hi << 4) | lo);
      }
      return pdfBytesToText(raw);
    }
    if (isWhitespace(byte)) {
      i += 1;
      continue;
    }
    if (hexValue(byte) < 0) return null;
    digits.push(byte);
    i += 1;
  }
  return null;
}

function readPdfString(bytes: Uint8Array, index: number): string | null {
  const marker = bytes[index];
  if (marker === 0x28) return readLiteral(bytes, index);
  if (marker === 0x3c) return readHex(bytes, index);
  return null;
}

/** Instant from the last parseable `/CreationDate` in a PDF Info dictionary. Null if absent or unreadable. */
export function parsePdfCreationInstant(bytes: Uint8Array): Date | null {
  try {
    let found: Date | null = null;
    let from = 0;
    while (from <= bytes.length - CREATION_DATE.length) {
      const at = indexOfBytes(bytes, CREATION_DATE, from);
      if (at < 0) break;
      const after = at + CREATION_DATE.length;
      if (!isDelimiter(bytes[after])) {
        from = after;
        continue;
      }
      const token = readPdfString(bytes, skipSpaceAndComments(bytes, after));
      if (token != null) {
        const instant = parsePdfDateToken(token);
        if (instant != null && !Number.isNaN(instant.getTime())) found = instant;
      }
      from = after;
    }
    return found;
  } catch {
    return null;
  }
}

function isUsableLastModified(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * New-attachment `payloads[].attachment.creation`.
 * File.lastModified as Copenhagen `YYYY-MM-DDTHH:mm:ss±HH:mm`.
 * Missing, 0 or non-finite lastModified → storage object `created_at`
 * (on the compose path that is the attach instant; there is no storage object).
 */
export function attachmentCreationTime(file: {
  lastModified?: number | null;
  createdAt?: Date | string;
}): string {
  if (isUsableLastModified(file.lastModified)) {
    return formatCopenhagenFhirDateTime(new Date(file.lastModified));
  }
  if (typeof file.createdAt === 'string' && file.createdAt.length > 0) {
    const parsed = new Date(file.createdAt);
    if (!Number.isNaN(parsed.getTime())) return formatCopenhagenFhirDateTime(parsed);
  }
  if (file.createdAt instanceof Date && !Number.isNaN(file.createdAt.getTime())) {
    return formatCopenhagenFhirDateTime(file.createdAt);
  }
  return formatCopenhagenFhirDateTime(new Date());
}
